import IMask from 'imask';

/**
 * Reguły numeru krajowego (bez kierunkowego) wg ISO country.
 * `digits` = max / dokładna długość; opcjonalne `min` dla krajów o zmiennej długości.
 */
const PHONE_RULES = {
  PL: { digits: 9, mask: '000 000 000', placeholder: '123 456 789' },
  GB: { digits: 10, mask: '0000 000000', placeholder: '7911 123456' },
  US: { digits: 10, mask: '000 000 0000', placeholder: '201 555 0123' },
  CA: { digits: 10, mask: '000 000 0000', placeholder: '201 555 0123' },
  DE: { digits: 11, min: 6, mask: '000 00000000', placeholder: '151 23456789' },
  CZ: { digits: 9, mask: '000 000 000', placeholder: '601 123 456' },
  SK: { digits: 9, mask: '000 000 000', placeholder: '901 123 456' },
  AT: { digits: 13, min: 4, mask: '000 0000000000', placeholder: '664 1234567' },
  UA: { digits: 9, mask: '000 000 000', placeholder: '501 234 567' },
  NL: { digits: 9, mask: '000 000 000', placeholder: '612 345 678' },
  BE: { digits: 9, min: 8, mask: '000 000 000', placeholder: '470 123 456' },
  FR: { digits: 9, mask: '000 000 000', placeholder: '612 345 678' },
  IT: { digits: 10, min: 9, mask: '000 000 0000', placeholder: '312 345 6789' },
  HU: { digits: 9, min: 8, mask: '000 000 000', placeholder: '201 234 567' },
  LT: { digits: 8, mask: '000 00000', placeholder: '612 34567' },
  LV: { digits: 8, mask: '000 00000', placeholder: '212 34567' },
  EE: { digits: 8, min: 7, mask: '000 00000', placeholder: '512 34567' },
  SE: { digits: 12, min: 7, mask: '000 000000000', placeholder: '701 234567' },
  NO: { digits: 8, mask: '000 00000', placeholder: '406 12345' },
  DK: { digits: 8, mask: '000 00000', placeholder: '201 23456' },
  IE: { digits: 9, min: 7, mask: '000 000 000', placeholder: '851 234 567' },
  ES: { digits: 9, mask: '000 000 000', placeholder: '612 345 678' },
  PT: { digits: 9, mask: '000 000 000', placeholder: '912 345 678' },
  CH: { digits: 9, mask: '000 000 000', placeholder: '781 234 567' },
  RO: { digits: 9, mask: '000 000 000', placeholder: '712 345 678' },
  BG: { digits: 9, min: 8, mask: '000 000 000', placeholder: '872 123 456' },
  HR: { digits: 9, min: 8, mask: '000 000 000', placeholder: '912 345 678' },
  SI: { digits: 8, mask: '000 00000', placeholder: '312 34567' },
  FI: { digits: 12, min: 5, mask: '000 000000000', placeholder: '401 234567' },
};

const DEFAULT_RULE = {
  digits: 15,
  min: 6,
  mask: '000 000 000 000 000',
  placeholder: '123 456 789',
};

/** Domyślnie PL (wsteczna kompatybilność eksportów). */
export const BOOKING_PHONE_MASK = PHONE_RULES.PL.mask;
export const BOOKING_PHONE_DIGITS = PHONE_RULES.PL.digits;

export const getBookingPhoneRule = (country) => {
  const code = String(country || 'PL').toUpperCase();
  return PHONE_RULES[code] || DEFAULT_RULE;
};

const readInputCountry = (input) => {
  const control = input?.closest?.('.booking-form__control--phone') || input?.closest?.('.booking-form__field');
  const select = control?.querySelector?.('.js-phone-prefix, [name="phone-prefix"]');
  const code = String(select?.selectedOptions?.[0]?.dataset?.country || '').toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : 'PL';
};

const readInputPrefixDigits = (input) => {
  const control = input?.closest?.('.booking-form__control--phone') || input?.closest?.('.booking-form__field');
  const select = control?.querySelector?.('.js-phone-prefix, [name="phone-prefix"]');
  return String(select?.value || '+48').replace(/\D/g, '');
};

const titleFromRule = (rule) => {
  if (rule.min != null && rule.min !== rule.digits) {
    return `Podaj numer telefonu (${rule.min}–${rule.digits} cyfr, bez kierunkowego)`;
  }
  return `Podaj ${rule.digits}-cyfrowy numer telefonu (bez kierunkowego)`;
};

const normalizeNationalDigits = (raw, { maxDigits, prefixDigits = '' } = {}) => {
  let digits = String(raw || '').replace(/\D/g, '');
  if (!digits) return '';

  const max = maxDigits || DEFAULT_RULE.digits;
  const prefix = String(prefixDigits || '').replace(/\D/g, '');

  // Wklejony numer z +XX / 00XX / XX…
  if (prefix) {
    if (digits.startsWith(`00${prefix}`) && digits.length > prefix.length + 2) {
      digits = digits.slice(prefix.length + 2);
    } else if (
      digits.startsWith(prefix)
      && digits.length > prefix.length
      && digits.length - prefix.length >= Math.min(6, max)
    ) {
      digits = digits.slice(prefix.length);
    }
  }

  // Trunk prefix (np. 0 w UK) — zrzucamy wiodące zera z numeru krajowego.
  digits = digits.replace(/^0+/, '') || digits;
  return digits.slice(0, max);
};

const applyRuleToInput = (input, mask, rule) => {
  input._bookingPhoneRule = rule;
  mask.updateOptions({ mask: rule.mask });
  input.setAttribute('inputmode', 'numeric');
  input.setAttribute('maxlength', String(rule.mask.length));
  // Luźny pattern HTML — dokładną długość sprawdza isCompleteBookingPhone (kraje o zmiennej długości).
  input.setAttribute('pattern', `[0-9\\s]{${rule.min != null ? rule.min : rule.digits},${rule.mask.length}}`);
  input.setAttribute('title', titleFromRule(rule));
  input.placeholder = rule.placeholder;

  const prefixDigits = readInputPrefixDigits(input);
  const digits = normalizeNationalDigits(mask.unmaskedValue || input.value, {
    maxDigits: rule.digits,
    prefixDigits,
  });
  mask.unmaskedValue = digits;
  mask.updateValue();
};

export const createBookingPhoneMask = (input) => {
  if (!input) return null;
  if (input._bookingPhoneMask) return input._bookingPhoneMask;

  const initialRule = getBookingPhoneRule(readInputCountry(input));
  const mask = IMask(input, {
    mask: initialRule.mask,
    lazy: true,
  });

  input._bookingPhoneMask = mask;
  applyRuleToInput(input, mask, initialRule);

  const applyDigits = (raw) => {
    const rule = input._bookingPhoneRule || getBookingPhoneRule(readInputCountry(input));
    const digits = normalizeNationalDigits(raw, {
      maxDigits: rule.digits,
      prefixDigits: readInputPrefixDigits(input),
    });
    mask.unmaskedValue = digits;
    mask.updateValue();
  };

  input.addEventListener('paste', (event) => {
    const text = event.clipboardData?.getData('text');
    if (!text) return;
    event.preventDefault();
    applyDigits(text);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  });

  // Po hydracji z draftu / syncu wartości bez maski.
  input.addEventListener('booking-phone:refresh', () => {
    applyDigits(input.value);
  });

  input._bookingPhoneApplyCountry = (country) => {
    applyRuleToInput(input, mask, getBookingPhoneRule(country));
  };

  return mask;
};

/** Aktualizuje maskę / maxlength po zmianie kierunkowego. */
export const setBookingPhoneCountry = (input, country) => {
  if (!input) return;
  if (!input._bookingPhoneMask) createBookingPhoneMask(input);
  input._bookingPhoneApplyCountry?.(country || readInputCountry(input));
};

export const destroyBookingPhoneMask = (maskOrInput) => {
  const mask = maskOrInput?._bookingPhoneMask || maskOrInput;
  mask?.destroy?.();
  if (maskOrInput?._bookingPhoneMask) {
    delete maskOrInput._bookingPhoneMask;
    delete maskOrInput._bookingPhoneRule;
    delete maskOrInput._bookingPhoneApplyCountry;
  }
};

export const bookingPhoneUnmasked = (input) => {
  const mask = input?._bookingPhoneMask;
  if (mask) return String(mask.unmaskedValue || '').replace(/\D/g, '');
  const rule = getBookingPhoneRule(readInputCountry(input));
  return normalizeNationalDigits(input?.value, {
    maxDigits: rule.digits,
    prefixDigits: readInputPrefixDigits(input),
  });
};

export const isCompleteBookingPhone = (input) => {
  const digits = bookingPhoneUnmasked(input);
  const rule = input?._bookingPhoneRule || getBookingPhoneRule(readInputCountry(input));
  const min = rule.min != null ? rule.min : rule.digits;
  return digits.length >= min && digits.length <= rule.digits;
};

export const bookingPhoneValidityMessage = (input) => {
  const rule = input?._bookingPhoneRule || getBookingPhoneRule(readInputCountry(input));
  return titleFromRule(rule);
};
